'use client';

import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { replacePlaceholders } from '@/helper/replacements';
import { isStoryblokEditorContext } from '@/helper/storyblok/isStoryblokEditorContext';
import { getVtwReplacementTexts } from '@/helper/vtwutils';
import { useUserStore } from '@/providers/UserStoreProvider';
import HandCoinOutline from '@/public/icons/hand-coin-outline.svg';
import LinkVariantPlus from '@/public/icons/link-variant-plus.svg';
import type { UserState } from '@/stores/UserStore';
import PartialLoginButton from '@/storyblok/partialLoginButton/PartialLoginButton';
import type { ComponentRecommendationWidgetStoryblok } from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import Script from 'next/script';
import { useMemo } from 'react';

import classes from './RecommendationWidget.module.scss';
import { resolveRecommendationLoggedInView } from './recommendationWidgetView';
import { useRecommendationWidgetRuntime } from './useRecommendationWidgetRuntime';

const RecommendationWidget: React.FC<ComponentRecommendationWidgetStoryblok> = (props) => {
  const {
    _uid,
    button_container_id,
    widget_url,
    t_mc,
    t_art_number,
    redirect_url,
    headline,
    description,
    has_customer_context,
    LoggedOutSubheadline,
    LoggedOutHeadline,
    LoggedOutText,
    LoggedOutButtonText,
    LoggedInSubheadline,
    loggedInTextHeadline,
    LoggedInText,
    LoggedInButtonText,
    CardHeadline,
    CardText,
    CardButtonText,
    simulateLoggedIn,
  } = props;

  const { loggedIn, name } = useUserStore((state) => ({
    loggedIn: state.loggedIn,
    name: state.name,
  }));

  const { loading, containerId, widgetSrc, handleCtaClick } = useRecommendationWidgetRuntime({
    _uid,
    button_container_id,
    widget_url,
    t_mc,
    t_art_number,
    redirect_url,
  });

  const isStoryblokEditor = isStoryblokEditorContext();
  const editable = useMemo(() => storyblokEditable(props), [props]);

  const isCustomerContextEnabled = Boolean(has_customer_context);
  const isLoggedInView = resolveRecommendationLoggedInView({
    hasCustomerContext: isCustomerContextEnabled,
    loggedIn,
    simulateLoggedIn,
    isStoryblokEditor,
  });

  const replacements = useMemo(
    () => getVtwReplacementTexts({ loggedIn: isLoggedInView, name } as UserState),
    [isLoggedInView, name]
  );
  const applyReplacements = (text?: string) => (text ? replacePlaceholders(replacements, text) : '');

  const showWidget = !isCustomerContextEnabled || isLoggedInView;
  const isDarkCard = isLoggedInView;

  let subheadline = '';
  let contentHeadline = '';
  let bodyText = '';
  let ctaLabel = '';
  let showLoginButton = false;

  if (!isCustomerContextEnabled) {
    contentHeadline = CardHeadline ?? '';
    bodyText = applyReplacements(CardText);
    ctaLabel = CardButtonText ?? '';
  } else if (isLoggedInView) {
    subheadline = LoggedInSubheadline ?? '';
    contentHeadline = loggedInTextHeadline ?? '';
    bodyText = applyReplacements(LoggedInText);
    ctaLabel = LoggedInButtonText ?? '';
  } else {
    subheadline = LoggedOutSubheadline ?? '';
    contentHeadline = LoggedOutHeadline ?? '';
    bodyText = applyReplacements(LoggedOutText);
    showLoginButton = Boolean(LoggedOutButtonText);
  }

  return (
    <>
      <LoadingBackdrop loading={loading} type="card" />
      {!loading && (
        <div {...editable} data-testid="recommendation-widget" className={classes.recommendationWidget}>
          <div className={classes.intro}>
            <div className={classes.imageWrapper} data-testid="recommendation-widget-image-wrapper">
              <HandCoinOutline aria-hidden="true" />
            </div>
            <div className={classes.descriptionBlock}>
              <HeadlineField className={classes.headline} tag={HeadlineTag.H2}>
                {headline}
              </HeadlineField>
              <p className={classes.description}>{description}</p>
            </div>
          </div>

          <div className={classNames(classes.content, isDarkCard && classes.loggedIn)}>
            {subheadline && <div className={classes.richTextHeadline}>{subheadline}</div>}
            {contentHeadline && (
              <HeadlineField className={classes.contentHeadline} tag={HeadlineTag.H4}>
                {contentHeadline}
              </HeadlineField>
            )}
            {bodyText && <p className={classes.contentText}>{bodyText}</p>}
            {showLoginButton && (
              <PartialLoginButton
                className={classes.loginButton}
                blok={{
                  _uid: `${_uid}-login-button`,
                  component: 'partial_login_button',
                  label: LoggedOutButtonText,
                }}
              />
            )}
            {showWidget && ctaLabel && (
              <Button
                variant="ctaFull"
                onClick={handleCtaClick}
                className={classes.ctaButton}
                label={ctaLabel}
                startIcon={<LinkVariantPlus />}
              />
            )}
            {showWidget && widgetSrc && <Script src={widgetSrc} strategy="afterInteractive" />}
          </div>
          {showWidget && <div id={containerId} className={classes.hiddenWidgetContainer} />}
        </div>
      )}
    </>
  );
};

export default RecommendationWidget;
